/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, InjectionToken, NgZone} from '@angular/core';

import {getDOM} from '../dom_adapter';
/**
 * \@stable
 */
export const EVENT_MANAGER_PLUGINS =
    new InjectionToken<EventManagerPlugin[]>('EventManagerPlugins');
/**
 * \@stable
 */
export class EventManager {
private _plugins: EventManagerPlugin[];
private _eventNameToPlugin = new Map<string, EventManagerPlugin>();
/**
 * @param {?} plugins
 * @param {?} _zone
 */
constructor( plugins: EventManagerPlugin[],
private _zone: NgZone) {
    plugins.forEach(p => p.manager = this);
    this._plugins = plugins.slice().reverse();
  }
/**
 * @param {?} element
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addEventListener(element: HTMLElement, eventName: string, handler: Function): Function {
    const /** @type {?} */ plugin = this._findPluginFor(eventName);
    return plugin.addEventListener(element, eventName, handler);
  }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addGlobalEventListener(target: string, eventName: string, handler: Function): Function {
    const /** @type {?} */ plugin = this._findPluginFor(eventName);
    return plugin.addGlobalEventListener(target, eventName, handler);
  }
/**
 * @return {?}
 */
getZone(): NgZone { return this._zone; }
/**
 * \@internal
 * @param {?} eventName
 * @return {?}
 */
_findPluginFor(eventName: string): EventManagerPlugin {
    const /** @type {?} */ plugin = this._eventNameToPlugin.get(eventName);
    if (plugin) {
      return plugin;
    }

    const /** @type {?} */ plugins = this._plugins;
    for (let /** @type {?} */ i = 0; i < plugins.length; i++) {
      const /** @type {?} */ plugin = plugins[i];
      if (plugin.supports(eventName)) {
        this._eventNameToPlugin.set(eventName, plugin);
        return plugin;
      }
    }
    throw new Error(`No event manager plugin found for event ${eventName}`);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Inject, args: [EVENT_MANAGER_PLUGINS, ] }, ]},
{type: NgZone, },
];
}

function EventManager_tsickle_Closure_declarations() {
/** @type {?} */
EventManager.decorators;
/**
 * @nocollapse
 * @type {?}
 */
EventManager.ctorParameters;
/** @type {?} */
EventManager.prototype._plugins;
/** @type {?} */
EventManager.prototype._eventNameToPlugin;
/** @type {?} */
EventManager.prototype._zone;
}

/**
 * @abstract
 */
export abstract class EventManagerPlugin {
/**
 * @param {?} _doc
 */
constructor(private _doc: any) {}

  manager: EventManager;
/**
 * @abstract
 * @param {?} eventName
 * @return {?}
 */
supports(eventName: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addEventListener(element: HTMLElement, eventName: string, handler: Function) {}
/**
 * @param {?} element
 * @param {?} eventName
 * @param {?} handler
 * @return {?}
 */
addGlobalEventListener(element: string, eventName: string, handler: Function): Function {
    const /** @type {?} */ target: HTMLElement = getDOM().getGlobalEventTarget(this._doc, element);
    if (!target) {
      throw new Error(`Unsupported event target ${target} for event ${eventName}`);
    }
    return this.addEventListener(target, eventName, handler);
  }
}

function EventManagerPlugin_tsickle_Closure_declarations() {
/** @type {?} */
EventManagerPlugin.prototype.manager;
/** @type {?} */
EventManagerPlugin.prototype._doc;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
